import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

//Bootstrap
import { Card, Form, Spinner } from "react-bootstrap";

//Icons
import { MdEdit } from "react-icons/md";
import { MdOutlineStar } from "react-icons/md";
import { MdClose } from "react-icons/md";

//UI components
import Field from "../../../ui/Field";
import DeleteModal from "../../../ui/DeleteModal";
import ErrorMessage from "../../../ui/ErrorMessage";

//components
import ClaniceNaProjektuCardItemEditForm from "./ClaniceNaProjektuCardItemEditForm";

//API calls
import {
  useDeleteClanicaUMFromProject,
  useUpdateSodelujoceOsebaTipOsebe,
  useDeleteSodelujoceOseba,
  useAddSodelujoceOsebaToClanica,
} from "../../../../services/ProjectDetails/partnerskeOrganizacijeAPI";
import { useGetProjectSplosnoDetails } from "../../../../services/ProjectDetails/splosnoAPI";
import {
  useGetOsebeFromImenik,
  useGetVuirSifrant,
} from "../../../../services/Shared/sharedAPI";

//Auth
import RoleGuard from "../../../../auth/RoleGuard";
import { MODULES, ROLE_GROUPS } from "../../../../auth/roles";
import useClanicaCheck from "../../../../auth/useClanicaCheck";
import useRoleCheck from "../../../../auth/useRoleCheck";

//Utils
import { useDebounceHook } from "../../../utils/useDebounceHook";
import formatDateHelper from "../../../utils/dateFormat";

const normalizeDateValue = (dateValue) => dateValue?.slice(0, 10) || "";
const getTipOsebeLabel = (oseba, vuirSifrant) => {
  if (oseba?.tipOsebe) {
    return oseba.tipOsebe;
  }

  const tipId = Number(oseba?.tipOsebeID ?? oseba?.tipOsebeId);
  if (!tipId) {
    return "";
  }

  return vuirSifrant.find((tip) => Number(tip.value) === tipId)?.label || "";
};

const ClaniceNaProjektuCardItem = ({ clanica }) => {
  const { id } = useParams();

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isAddingPerson, setIsAddingPerson] = useState(false);
  const [searchTermOseba, setSearchTermOseba] = useState("");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedTipOsebe, setSelectedTipOsebe] = useState("");
  const [selectedOd, setSelectedOd] = useState("");
  const [selectedDo, setSelectedDo] = useState("");
  const [addPersonValidationError, setAddPersonValidationError] = useState("");
  const [editingPersonId, setEditingPersonId] = useState(null);
  const [editedTipOsebe, setEditedTipOsebe] = useState("");
  const [editedOd, setEditedOd] = useState("");
  const [editedDo, setEditedDo] = useState("");
  const [editPersonValidationError, setEditPersonValidationError] =
    useState("");

  const { data: project } = useGetProjectSplosnoDetails(id);
  const { isUserFromClanica } = useClanicaCheck(clanica?.clanicaID);
  const { isEditor } = useRoleCheck(MODULES.PROJEKTI);
  const projectStartDate = normalizeDateValue(project?.zacetek);
  const projectEndDate = normalizeDateValue(project?.konec);
  const isDateWithinProjectRange =
    (!projectStartDate || selectedOd >= projectStartDate) &&
    (!projectEndDate || selectedDo <= projectEndDate);
  const isDateOrderValid =
    !selectedOd || !selectedDo || selectedOd <= selectedDo;
  const isAddPersonDateRangeValid =
    !!selectedOd &&
    !!selectedDo &&
    isDateWithinProjectRange &&
    isDateOrderValid;
  const isEditedDateWithinProjectRange =
    (!projectStartDate || editedOd >= projectStartDate) &&
    (!projectEndDate || editedDo <= projectEndDate);
  const isEditedDateOrderValid = !editedOd || !editedDo || editedOd <= editedDo;
  const isEditPersonDateRangeValid =
    !!editedOd &&
    !!editedDo &&
    isEditedDateWithinProjectRange &&
    isEditedDateOrderValid;

  let addPersonDateValidationMessage = "";
  if (selectedOd && projectStartDate && selectedOd < projectStartDate) {
    addPersonDateValidationMessage = `Datum Od ne sme biti pred začetkom projekta (${projectStartDate}).`;
  } else if (selectedDo && projectEndDate && selectedDo > projectEndDate) {
    addPersonDateValidationMessage = `Datum Do ne sme biti po koncu projekta (${projectEndDate}).`;
  } else if (selectedOd && selectedDo && selectedOd > selectedDo) {
    addPersonDateValidationMessage = "Datum Od ne sme biti po datumu Do.";
  }

  let editPersonDateValidationMessage = "";
  if (editedOd && projectStartDate && editedOd < projectStartDate) {
    editPersonDateValidationMessage = `Datum Od ne sme biti pred začetkom projekta (${projectStartDate}).`;
  } else if (editedDo && projectEndDate && editedDo > projectEndDate) {
    editPersonDateValidationMessage = `Datum Do ne sme biti po koncu projekta (${projectEndDate}).`;
  } else if (editedOd && editedDo && editedOd > editedDo) {
    editPersonDateValidationMessage = "Datum Od ne sme biti po datumu Do.";
  }

  useEffect(() => {
    if (!isAddingPerson) {
      return;
    }

    if (!selectedOd) {
      setSelectedOd(normalizeDateValue(project?.zacetek));
    }

    if (!selectedDo) {
      setSelectedDo(normalizeDateValue(project?.konec));
    }
  }, [
    isAddingPerson,
    project?.konec,
    project?.zacetek,
    selectedDo,
    selectedOd,
  ]);

  //GET osebe from Imenik based on search term
  const { data: osebeImenik, isLoading: isLoadingOsebe } =
    useGetOsebeFromImenik(useDebounceHook(searchTermOseba, 300));

  //GET VUIR sifrant for academic person types
  const { data: vuirSifrant = [] } = useGetVuirSifrant();

  //DELETE clanica UM from project
  const {
    mutate: deleteClanicaUM,
    isPending: isPendingDeleteClanica,
    isError,
    error,
    reset: resetMutationError,
  } = useDeleteClanicaUMFromProject(id, clanica.clanicaID);

  //PUT update TipOsebe for sodelujoča oseba
  const {
    mutate: updateTipOsebe,
    isPending: isPendingUpdate,
    isError: isErrorUpdate,
    error: errorUpdate,
    reset: resetUpdateError,
  } = useUpdateSodelujoceOsebaTipOsebe(id);

  //DELETE sodelujoča oseba from clanica
  const {
    mutate: deleteSodelujoceOseba,
    isPending: isPendingDelete,
    isError: isErrorDelete,
    error: errorDelete,
    reset: resetDeleteError,
  } = useDeleteSodelujoceOseba(id);

  //POST add sodelujoča oseba to clanica
  const {
    mutate: addSodelujoceOseba,
    isPending: isPendingAdd,
    isError: isErrorAdd,
    error: errorAdd,
    reset: resetAddError,
  } = useAddSodelujoceOsebaToClanica(id);

  const onEditClick = () => {
    setIsEditing(true);
  };

  const onDeleteClanicaClick = () => {
    deleteClanicaUM({
      onSuccess: () => {
        setShowDeleteModal(false);
      },
      onError: () => {
        setTimeout(() => {
          resetMutationError();
        }, 3000);
        setShowDeleteModal(false);
      },
    });
  };

  const handleAddPersonClick = () => {
    setAddPersonValidationError("");
    setSelectedOd(normalizeDateValue(project?.zacetek));
    setSelectedDo(normalizeDateValue(project?.konec));
    setIsAddingPerson(true);
  };

  const handleCancelAddPerson = () => {
    setIsAddingPerson(false);
    setSearchTermOseba("");
    setSelectedPerson(null);
    setSelectedTipOsebe("");
    setSelectedOd("");
    setSelectedDo("");
    setAddPersonValidationError("");
    resetAddError();
  };

  const handleSelectPerson = (oseba) => {
    setSelectedPerson(oseba);
    setSearchTermOseba("");
    setAddPersonValidationError("");
  };

  const handleAddSodelujocoOsebo = () => {
    if (!isAddPersonDateRangeValid) {
      setAddPersonValidationError(
        addPersonDateValidationMessage ||
          "Datuma Od in Do morata biti znotraj trajanja projekta.",
      );
      return;
    }

    setAddPersonValidationError("");
    const payload = {
      kisID: selectedPerson.kisid,
      naziv: selectedPerson.naziv,
      clanica: selectedPerson.kratica,
      od: selectedOd,
      do: selectedDo,
      tipOsebe: Number(selectedTipOsebe),
    };

    addSodelujoceOseba(
      {
        clanicaID: clanica.clanicaID,
        payload,
      },
      {
        onSuccess: () => {
          handleCancelAddPerson();
        },
        onError: () => {
          setTimeout(() => {
            resetAddError();
          }, 3000);
        },
      },
    );
  };
  const handleRemovePerson = (oseba) => {
    deleteSodelujoceOseba(
      {
        kisID: oseba.kisID,
        clanicaID: clanica.clanicaID,
      },
      {
        onSuccess: () => {
          // Success message will show via toast or data refresh
        },
        onError: () => {
          setTimeout(() => {
            resetDeleteError();
          }, 3000);
        },
      },
    );
  };

  const handleEditPerson = (oseba) => {
    setEditingPersonId(oseba.sodelujoceOsebeID);
    setEditedTipOsebe(oseba.tipOsebeID || "");
    setEditedOd(normalizeDateValue(oseba.od_ || oseba.od || project?.zacetek));
    setEditedDo(normalizeDateValue(oseba.do_ || oseba.do || project?.konec));
    setEditPersonValidationError("");
  };

  const handleCancelEditPerson = () => {
    setEditingPersonId(null);
    setEditedTipOsebe("");
    setEditedOd("");
    setEditedDo("");
    setEditPersonValidationError("");
  };

  const handleSaveEditPerson = (oseba) => {
    if (!isEditPersonDateRangeValid) {
      setEditPersonValidationError(
        editPersonDateValidationMessage ||
          "Datuma Od in Do morata biti znotraj trajanja projekta.",
      );
      return;
    }

    const resolvedClanicaID =
      clanica?.clanicaID ??
      clanica?.ClanicaID ??
      oseba?.clanicaID ??
      oseba?.ClanicaID;
    const resolvedTipOsebeID = Number(editedTipOsebe);

    setEditPersonValidationError("");
    updateTipOsebe(
      {
        kisID: oseba.kisID,
        clanicaID: resolvedClanicaID,
        tipOsebeID: resolvedTipOsebeID,
        od: editedOd,
        do: editedDo,
      },
      {
        onSuccess: () => {
          handleCancelEditPerson();
        },
        onError: () => {
          setTimeout(() => {
            resetUpdateError();
          }, 3000);
        },
      },
    );
  };
  return (
    <>
      <Card className="mb-3 mt-3">
        <div className="d-flex align-items-center justify-content-between px-3 pt-3">
          <h5 className="mb-0">
            <span className="d-flex align-items-center gap-2">
              {clanica?.skrbnik && (
                <MdOutlineStar
                  title="Skrbnik projektne članice UM"
                  color="#ffc107"
                />
              )}
              {clanica?.clanica}
            </span>
          </h5>
          {!isEditing && (
            <RoleGuard
              moduleKey={MODULES.PROJEKTI}
              roles={ROLE_GROUPS.EDITORS_AND_SKRBNIK}
              vodilnaClanicaProjektaID={project?.clanicaID}
            >
              <div>
                <button
                  className="btn btn-sm btn-outline-primary uredi-btn-tab-card-header border-0"
                  onClick={onEditClick}
                >
                  <span className="d-flex align-items-center gap-1">
                    Uredi <MdEdit />
                  </span>
                </button>
                {/** We only show delete button for clanice that are not skrbnik (NOT CONNECTED WITH ROLEGUARD AND USER ROLES) */}
                {!clanica?.skrbnik && (
                  <button
                    className="btn btn-sm btn-outline-danger border-0"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    <span className="d-flex align-items-center gap-1">
                      Izbriši
                    </span>
                  </button>
                )}
              </div>
            </RoleGuard>
          )}
        </div>
        {!isEditing && (
          <div className="px-3 pb-3">
            <div className="mb-1">
              <Field
                label="Vloga članice"
                value={clanica?.vloga}
                showLabel={false}
                className="mb-2 text-primary fw-bold"
              />
            </div>
            <div className="mb-1">
              <Field
                label="Kontaktna oseba"
                className="mb-2"
                value={clanica?.kisID !== 0 ? clanica?.oseba : "Ni podatka"}
              />
            </div>

            {/* Sodelujoče osebe */}
            <RoleGuard
              moduleKey={MODULES.PROJEKTI}
              roles={ROLE_GROUPS.GLOBALNI_AND_FAKULTETNI}
            >
              <div className="mt-3">
                <h6
                  className="text-muted mb-2 text-uppercase"
                  style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}
                >
                  Sodelujoče osebe
                </h6>
                {clanica.sodelujociOsebe &&
                clanica.sodelujociOsebe.length > 0 ? (
                  <div className="border-top">
                    {clanica.sodelujociOsebe.map((oseba) => (
                      <div
                        key={oseba.sodelujoceOsebeID}
                        className="py-2 border-bottom"
                      >
                        {editingPersonId === oseba.sodelujoceOsebeID ? (
                          // Edit mode
                          <div className="small">
                            <div className="mb-2 text-body-secondary">
                              {oseba.oseba}
                            </div>
                            {getTipOsebeLabel(oseba, vuirSifrant) && (
                              <div
                                className="text-muted mb-2"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {getTipOsebeLabel(oseba, vuirSifrant)}
                              </div>
                            )}
                            <div className="row g-2 align-items-end">
                              <div className="col-sm-4">
                                <Form.Group className="mb-0">
                                  <Form.Label
                                    className="text-muted mb-1"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    Tip osebe (VUIR)
                                  </Form.Label>
                                  <Form.Select
                                    size="sm"
                                    value={editedTipOsebe}
                                    onChange={(e) =>
                                      setEditedTipOsebe(e.target.value)
                                    }
                                  >
                                    {vuirSifrant.map((tip) => (
                                      <option key={tip.value} value={tip.value}>
                                        {tip.label}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              </div>
                              <div className="col-sm-3">
                                <Form.Group className="mb-0">
                                  <Form.Label
                                    className="text-muted mb-1"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    Od
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    type="date"
                                    value={editedOd}
                                    onChange={(e) => {
                                      setEditedOd(e.target.value);
                                      setEditPersonValidationError("");
                                    }}
                                    min={projectStartDate || undefined}
                                    max={projectEndDate || undefined}
                                  />
                                </Form.Group>
                              </div>
                              <div className="col-sm-3">
                                <Form.Group className="mb-0">
                                  <Form.Label
                                    className="text-muted mb-1"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    Do
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    type="date"
                                    value={editedDo}
                                    onChange={(e) => {
                                      setEditedDo(e.target.value);
                                      setEditPersonValidationError("");
                                    }}
                                    min={projectStartDate || undefined}
                                    max={projectEndDate || undefined}
                                  />
                                </Form.Group>
                              </div>
                              <div className="col-sm-2">
                                <div className="d-flex gap-2 justify-content-sm-end">
                                  <button
                                    className="btn btn-sm btn-link px-0 text-decoration-none"
                                    onClick={() => handleSaveEditPerson(oseba)}
                                    disabled={
                                      !editedTipOsebe ||
                                      !isEditPersonDateRangeValid ||
                                      isPendingUpdate
                                    }
                                  >
                                    {isPendingUpdate
                                      ? "Shranjevanje..."
                                      : "Shrani"}
                                  </button>
                                  <button
                                    className="btn btn-sm btn-link px-0 text-muted text-decoration-none"
                                    onClick={handleCancelEditPerson}
                                    disabled={isPendingUpdate}
                                  >
                                    Prekliči
                                  </button>
                                </div>
                              </div>
                            </div>
                            {editPersonDateValidationMessage && (
                              <div className="alert alert-warning small mt-2 mb-0">
                                {editPersonDateValidationMessage}
                              </div>
                            )}
                            {editPersonValidationError && (
                              <div className="alert alert-danger small mt-2 mb-0">
                                {editPersonValidationError}
                              </div>
                            )}
                            {isErrorUpdate && (
                              <div className="alert alert-danger small mt-2 mb-0">
                                {errorUpdate?.message ||
                                  "Prišlo je do napake pri shranjevanju"}
                              </div>
                            )}
                          </div>
                        ) : (
                          // Display mode
                          <div className="d-flex align-items-start justify-content-between gap-3 small">
                            <div className="d-flex flex-column flex-grow-1">
                              <span>{oseba.oseba}</span>
                              {getTipOsebeLabel(oseba, vuirSifrant) && (
                                <span
                                  className="text-muted"
                                  style={{ fontSize: "0.75rem" }}
                                >
                                  {getTipOsebeLabel(oseba, vuirSifrant)}
                                </span>
                              )}
                              {(oseba.od_ ||
                                oseba.do_ ||
                                oseba.od ||
                                oseba.do) && (
                                <span
                                  className="text-muted"
                                  style={{ fontSize: "0.75rem" }}
                                >
                                  {formatDateHelper(oseba.od_ || oseba.od) ||
                                    "-"}{" "}
                                  -{" "}
                                  {formatDateHelper(oseba.do_ || oseba.do) ||
                                    "-"}
                                </span>
                              )}
                            </div>
                            <RoleGuard
                              moduleKey={MODULES.PROJEKTI}
                              roles={ROLE_GROUPS.EDITORS_AND_SKRBNIK}
                            >
                              {(isEditor || isUserFromClanica) && (
                                <div className="d-flex gap-2 flex-shrink-0">
                                  <button
                                    className="btn btn-sm btn-link px-0 text-decoration-none"
                                    onClick={() => handleEditPerson(oseba)}
                                    disabled={isPendingDelete}
                                  >
                                    Uredi
                                  </button>
                                  <button
                                    className="btn btn-sm btn-link px-0 text-danger text-decoration-none"
                                    onClick={() => handleRemovePerson(oseba)}
                                    disabled={isPendingDelete}
                                  >
                                    {isPendingDelete
                                      ? "Odstranjevanje..."
                                      : "Odstrani"}
                                  </button>
                                </div>
                              )}
                            </RoleGuard>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted small mb-2">Ni sodelujočih oseb</p>
                )}

                {/* Add person search */}
                {isAddingPerson ? (
                  <div className="mt-2 pt-2 border-top small">
                    {!selectedPerson ? (
                      <>
                        <div className="position-relative">
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Iskanje osebe..."
                            value={searchTermOseba}
                            onChange={(e) => setSearchTermOseba(e.target.value)}
                            autoFocus
                          />
                          <button
                            className="btn btn-sm btn-link position-absolute top-50 end-0 translate-middle-y"
                            onClick={handleCancelAddPerson}
                            style={{ textDecoration: "none" }}
                          >
                            <MdClose />
                          </button>
                        </div>

                        {/* Search results dropdown */}
                        {searchTermOseba.length >= 3 && (
                          <div
                            className="border mt-1 bg-white small"
                            style={{ maxHeight: "200px", overflowY: "auto" }}
                          >
                            {isLoadingOsebe ? (
                              <div className="p-3 text-center">
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">Iskanje...</span>
                              </div>
                            ) : osebeImenik && osebeImenik.length > 0 ? (
                              osebeImenik.map((oseba) => (
                                <div
                                  key={oseba.kisid}
                                  className="px-2 py-2 border-bottom"
                                  onClick={() => handleSelectPerson(oseba)}
                                  style={{ cursor: "pointer" }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      "#f8f9fa")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      "white")
                                  }
                                >
                                  <div>{oseba.naziv}</div>
                                </div>
                              ))
                            ) : (
                              <div className="p-3 text-center text-muted">
                                Ni rezultatov
                              </div>
                            )}
                          </div>
                        )}
                        {searchTermOseba.length > 0 &&
                          searchTermOseba.length < 3 && (
                            <small className="text-muted d-block mt-1">
                              Vnesite vsaj 3 znake za iskanje
                            </small>
                          )}
                      </>
                    ) : (
                      <div className="pt-1 small">
                        <div className="mb-3">
                          <div
                            className="text-muted mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Izbrana oseba
                          </div>
                          <div>{selectedPerson.naziv}</div>
                        </div>

                        <Form.Group className="mb-3">
                          <Form.Label
                            className="text-muted mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Tip osebe (VUIR)
                          </Form.Label>
                          <Form.Select
                            size="sm"
                            value={selectedTipOsebe}
                            onChange={(e) =>
                              setSelectedTipOsebe(e.target.value)
                            }
                          >
                            {vuirSifrant.map((tip) => (
                              <option key={tip.value} value={tip.value}>
                                {tip.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label
                            className="text-muted mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Od
                          </Form.Label>
                          <Form.Control
                            size="sm"
                            type="date"
                            value={selectedOd}
                            onChange={(e) => setSelectedOd(e.target.value)}
                            min={projectStartDate}
                            max={projectEndDate || undefined}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label
                            className="text-muted mb-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Do
                          </Form.Label>
                          <Form.Control
                            size="sm"
                            type="date"
                            value={selectedDo}
                            onChange={(e) => setSelectedDo(e.target.value)}
                            min={projectStartDate || undefined}
                            max={projectEndDate}
                          />
                        </Form.Group>

                        {addPersonDateValidationMessage && (
                          <div className="alert alert-warning small mt-2 mb-3">
                            {addPersonDateValidationMessage}
                          </div>
                        )}

                        {addPersonValidationError && (
                          <div className="alert alert-danger small mt-2 mb-3">
                            {addPersonValidationError}
                          </div>
                        )}

                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-link px-0 text-decoration-none"
                            onClick={handleAddSodelujocoOsebo}
                            disabled={
                              !selectedTipOsebe ||
                              !isAddPersonDateRangeValid ||
                              isPendingAdd
                            }
                          >
                            {isPendingAdd ? "Dodajanje..." : "Dodaj"}
                          </button>
                          <button
                            className="btn btn-sm btn-link px-0 text-muted text-decoration-none"
                            onClick={handleCancelAddPerson}
                            disabled={isPendingAdd}
                          >
                            Prekliči
                          </button>
                        </div>
                        {isErrorAdd && (
                          <div className="alert alert-danger small mt-2 mb-0">
                            {errorAdd?.message ||
                              "Prišlo je do napake pri dodajanju"}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <RoleGuard
                    moduleKey={MODULES.PROJEKTI}
                    roles={ROLE_GROUPS.EDITORS_AND_SKRBNIK}
                  >
                    {(isEditor || isUserFromClanica) && (
                      <button
                        className="btn btn-sm btn-link px-0 mt-2 text-decoration-none"
                        onClick={handleAddPersonClick}
                      >
                        + Dodaj sodelujočo osebo
                      </button>
                    )}
                  </RoleGuard>
                )}
              </div>
            </RoleGuard>
          </div>
        )}
        {isEditing && (
          <ClaniceNaProjektuCardItemEditForm
            clanica={clanica}
            setIsEditing={setIsEditing}
          />
        )}
      </Card>

      {isError && (
        <ErrorMessage
          message={error?.message || "Prišlo je do napake"}
          show={isError}
        />
      )}

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={onDeleteClanicaClick}
        naziv={clanica.clanica}
        text="Ali ste prepričani, da želite iz projekta odstraniti sodelujočo članico UM:"
        isPending={isPendingDeleteClanica}
      />
    </>
  );
};

export default ClaniceNaProjektuCardItem;
